import * as THREE from 'three'
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js'
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter.js'
import JSZip from 'jszip'
import { AppObject, MaterialProperties, ModelUnit, STUDIO_EXTRAS_KEY, StudioExtras, materialParamsFrom } from '../types'
import { createTransformedGeometry } from './geometry'
import { compositePatternCanvas, loadImageElement, makeLogoTexture, makePatternTexture } from './patternTexture'
import { bodyMetrics, objectCenter, objectVForWorldY } from './patternUV'
import { findLogoObject } from './patternTemplate'
import { toLogoExtras } from './logoMesh'

/**
 * Carry a logo's placement in the node's glTF extras, so re-importing the file gives back
 * an editable logo instead of an anonymous mesh whose artwork gets mistaken for a pattern.
 */
function tagLogoNode(mesh: THREE.Mesh, obj: AppObject, scaleToMeters: number): void {
  if (!obj.logo) return
  mesh.userData = { ...mesh.userData, neatoLogo: toLogoExtras(obj.logo, scaleToMeters * 1000) }
}

/**
 * Record the studio settings on the object handed to the exporter, which writes userData as
 * glTF extras — on the scene, or on the root node when a group is exported. See StudioExtras.
 */
function tagStudio(root: THREE.Object3D, displayUnit: ModelUnit | undefined): void {
  if (!displayUnit) return
  const extras: StudioExtras = { version: 1, displayUnit }
  root.userData = { ...root.userData, [STUDIO_EXTRAS_KEY]: extras }
}

/**
 * The right export material for a part: logo artwork for logo parts, PBR + baked pattern
 * otherwise. Every kind reads the same MaterialProperties the preview draws that part with
 * (its own, else the shared one). Exported for tests.
 */
export function materialFor(
  obj: AppObject,
  objects: AppObject[],
  fallback: MaterialProperties
): Promise<THREE.MeshPhysicalMaterial> {
  const props = obj.material || fallback
  if (obj.logo) return createLogoMaterial(obj, props)
  if (obj.sourceMaterial) return Promise.resolve(createAuthoredExportMaterial(obj, props))
  return createExportMaterial(props, aspectOf(obj), alignVOf(obj, objects))
}

/**
 * Export material for a part that arrived with an authored PBR surface (see
 * AppObject.sourceMaterial). Its maps are written straight back out, with the panel's
 * values as the glTF factors that multiply them — so a re-export is a faithful copy of
 * the source until someone edits it, rather than a 4K asset flattened to one colour.
 */
function createAuthoredExportMaterial(obj: AppObject, props: MaterialProperties): THREE.MeshPhysicalMaterial {
  const src = obj.sourceMaterial!
  const material = new THREE.MeshPhysicalMaterial({
    ...materialParamsFrom(props),
    map: src.map,
    normalMap: src.normalMap,
    aoMap: src.aoMap,
    metalnessMap: src.metalnessMap,
    roughnessMap: src.roughnessMap,
    emissiveMap: src.emissiveMap,
    alphaMap: src.alphaMap,
  })
  if (src.normalScale) material.normalScale.copy(src.normalScale)
  material.aoMapIntensity = src.aoMapIntensity ?? 1
  return material
}

/** Circumference:height of a part, for keeping an unstretched pattern undistorted on export. */
function aspectOf(o: AppObject): number | undefined {
  const m = bodyMetrics(o)
  return m.height > 0 ? m.circumference / m.height : undefined
}

/**
 * The vertical anchor (0=top,1=bottom in this part's UV) the pattern aligns to.
 * Uses the chosen reference part (patternVAlignRef, or logo_main) — matching the preview.
 */
function alignVOf(obj: AppObject, objects: AppObject[]): number | undefined {
  const refId = obj.material?.patternVAlignRef || findLogoObject(objects)?.id
  const ref = objects.find((o) => o.id === refId)
  const refCenterY = ref ? objectCenter(ref)?.[1] : undefined
  return refCenterY !== undefined ? objectVForWorldY(obj, refCenterY) : obj.logoV
}

/**
 * Only the truly clear backdrop is cut away; everything with real coverage is left to
 * blend. A high cut-off would chop the anti-aliased flank off every stroke, and since
 * how much of a stroke survives then depends on the mip level, thin lettering picks up
 * an uneven, hand-drawn wobble as the logo shrinks on screen. (`transparent` makes the
 * exporter write alphaMode BLEND, so the GLB blends the same way everywhere.)
 */
export const LOGO_ALPHA_TEST = 0.02

/**
 * Material for a logo PART: the artwork with its alpha intact, cut out by alphaTest.
 * A logo is its own mesh, so — unlike a pattern — nothing is flattened over a base
 * color; the cut-out silhouette IS the shape the viewer sees.
 */
async function createLogoMaterial(obj: AppObject, props: MaterialProperties): Promise<THREE.MeshPhysicalMaterial> {
  const material = new THREE.MeshPhysicalMaterial({
    // The map is the artwork's shape as a white mask, so the logo is this colour —
    // exactly as in the preview.
    ...materialParamsFrom(props),
    // Deliberate exception, structural rather than a look: the artwork's alpha is the
    // silhouette, so the sheet is always transparent and cut out by alphaTest.
    // (polygonOffset is a preview-only depth bias; glTF has no field for it.)
    transparent: true,
    alphaTest: LOGO_ALPHA_TEST,
  })
  try {
    const img = await loadImageElement(obj.logo!.image)
    material.map = makeLogoTexture(img, obj.logo!.knockoutWhite ?? false)
    material.needsUpdate = true
  } catch (err) {
    console.warn('Failed to bake logo artwork into exported material:', err)
  }
  return material
}

async function createExportMaterial(
  matProps: MaterialProperties,
  objectAspect?: number,
  logoV?: number
): Promise<THREE.MeshPhysicalMaterial> {
  // specularIntensity is written as KHR_materials_specular, so a matte finish travels in
  // the GLB; envMapIntensity has no glTF field and is preview-only.
  const material = new THREE.MeshPhysicalMaterial(materialParamsFrom(matProps))

  // Bake the decorative pattern (flattened over the base color) into the
  // exported baseColor texture. Deliberate exception: the material color becomes white,
  // because the base colour is already baked into that texture — and the Product
  // Profile viewer's section-wrap shader, which multiplies the base map, then
  // reproduces the flattened pattern exactly. The preview does the same.
  if (matProps.patternImage) {
    try {
      const img = await loadImageElement(matProps.patternImage)
      const canvas = compositePatternCanvas(matProps.color, img, {
        repeatX: matProps.patternRepeatX ?? 1,
        repeatY: matProps.patternRepeatY ?? 1,
        offsetX: matProps.patternOffsetX ?? 0,
        offsetY: matProps.patternOffsetY ?? 0,
        scale: matProps.patternScale ?? 1,
        fill: matProps.patternFill ?? false,
        objectAspect,
        vAlign: matProps.patternVAlign ?? 'center',
        logoV,
      })
      material.map = makePatternTexture(canvas)
      material.color = new THREE.Color(0xffffff)
      material.needsUpdate = true
    } catch (err) {
      console.warn('Failed to bake pattern into exported material:', err)
    }
  }

  return material
}

/**
 * Export the model as a binary STL.
 *
 * STL carries geometry and nothing else — no materials, colours, names, UVs or parts. So
 * this is the route back into CAD or a slicer, not a way to move a finished look around;
 * everything the studio adds beyond shape is dropped, a generated logo included (its
 * sheet comes across as geometry, its artwork does not).
 *
 * Written in MILLIMETRES rather than the metres a GLB uses. STL has no unit field at all,
 * and millimetres is what CAD and slicers assume when they open one; exporting a 179mm
 * cup in metres would land it as a 0.179 unit speck.
 *
 * Binary, not ASCII: this model is 585k vertices, which ASCII STL would write as several
 * hundred megabytes of text.
 */
export async function exportAsSTL(objects: AppObject[], mmPerUnit: number, suggestedName = 'model.stl'): Promise<void> {
  const group = new THREE.Group()
  for (const obj of objects) {
    const geometry = createTransformedGeometry(obj, mmPerUnit, false)
    // Bake the part's move, in the same millimetres as the geometry it belongs to.
    if (obj.offset) {
      geometry.translate(obj.offset[0] * mmPerUnit, obj.offset[1] * mmPerUnit, obj.offset[2] * mmPerUnit)
    }
    group.add(new THREE.Mesh(geometry, new THREE.MeshBasicMaterial()))
  }

  const data = new STLExporter().parse(group, { binary: true }) as unknown as DataView
  // A view over the exporter's buffer rather than a copy — the binary payload is ~50
  // bytes a triangle, which on this scale of model is tens of megabytes.
  const bytes = new Uint8Array(data.buffer as ArrayBuffer, data.byteOffset, data.byteLength)
  await saveBlob(new Blob([bytes]), suggestedName, 'model/stl', '.stl')
}

export async function exportAsZip(
  objects: AppObject[],
  scaleToMeters: number,
  materialProps: MaterialProperties,
  suggestedName = 'parts.zip',
  displayUnit?: ModelUnit
): Promise<void> {
  const zip = new JSZip()
  const exporter = new GLTFExporter()

  for (const object of objects) {
    // Re-center each part individually for separate file exports
    const geometry = createTransformedGeometry(object, scaleToMeters, true)
    const scene = new THREE.Scene()
    // Use per-object material if available, otherwise global
    const material = await materialFor(object, objects, materialProps)
    const mesh = new THREE.Mesh(geometry, material)
    mesh.name = object.id
    tagLogoNode(mesh, object, scaleToMeters)
    scene.add(mesh)
    tagStudio(scene, displayUnit)

    const result = await exporter.parseAsync(scene, { binary: true })
    zip.file(`${object.id.replace(/[^a-z0-9]/gi, '_')}.glb`, result as ArrayBuffer)
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' })
  await saveBlob(zipBlob, suggestedName, 'application/zip', '.zip')
}

export async function exportAsSingleGLB(
  objects: AppObject[],
  scaleToMeters: number,
  materialProps: MaterialProperties,
  suggestedName = 'model.glb',
  displayUnit?: ModelUnit
): Promise<void> {
  // Faithful round-trip for GLB imports: re-export each part from its original
  // local geometry + node transform + name, applying only material changes.
  // This preserves each part's own coordinate frame (e.g. "logo_main" keeps its
  // wrap axis) and avoids baking rotations or dropping names into the output.
  const isGlbRoundTrip = objects.length > 0 && objects.every((o) => o.sourceTransform && o.sourceAttributes)
  if (isGlbRoundTrip) {
    await exportFaithfulGLB(objects, materialProps, suggestedName, scaleToMeters, displayUnit)
    return
  }

  const exporter = new GLTFExporter()
  const group = new THREE.Group()

  // First, create all geometries (already centered from load, just merge and scale)
  const meshes: THREE.Mesh[] = []
  for (const obj of objects) {
    const geometry = createTransformedGeometry(obj, scaleToMeters, false)
    // Apply the user's move (geometry is already scaled to meters).
    if (obj.offset) {
      geometry.translate(obj.offset[0] * scaleToMeters, obj.offset[1] * scaleToMeters, obj.offset[2] * scaleToMeters)
    }
    // Use per-object material if available, otherwise global
    const material = await materialFor(obj, objects, materialProps)
    const mesh = new THREE.Mesh(geometry, material)
    mesh.name = obj.id // preserve the part name in the exported node
    tagLogoNode(mesh, obj, scaleToMeters)
    meshes.push(mesh)
    group.add(mesh)
  }

  // Calculate bounding box of the entire merged assembly
  const groupBox = new THREE.Box3().setFromObject(group)
  const groupCenter = new THREE.Vector3()
  groupBox.getCenter(groupCenter)

  // Apply centering transformation to each mesh's vertex data
  meshes.forEach((mesh) => {
    const geom = mesh.geometry as THREE.BufferGeometry
    const posAttr = geom.getAttribute('position')
    for (let i = 0; i < posAttr.count; i++) {
      posAttr.setX(i, posAttr.getX(i) - groupCenter.x)
      posAttr.setZ(i, posAttr.getZ(i) - groupCenter.z)
      posAttr.setY(i, posAttr.getY(i) - groupBox.min.y)
    }
    posAttr.needsUpdate = true
  })

  tagStudio(group, displayUnit)
  const result = await exporter.parseAsync(group, { binary: true })
  const blob = new Blob([result as ArrayBuffer])
  await saveBlob(blob, suggestedName, 'model/gltf-binary', '.glb')
}

/**
 * Re-export a loaded GLB faithfully: each part is rebuilt from its original
 * local-space geometry and placed with its original node transform, keeping its
 * name. Only the material (e.g. color) reflects the user's edits. No centering,
 * scaling, or rotation is applied, so downstream tools that key off a specific
 * part (e.g. "logo_main") see the exact same coordinate frame as the source.
 */
async function exportFaithfulGLB(
  objects: AppObject[],
  materialProps: MaterialProperties,
  suggestedName: string,
  scaleToMeters = 1,
  displayUnit?: ModelUnit
): Promise<void> {
  const exporter = new GLTFExporter()
  const scene = new THREE.Scene()
  // A GLB's own coordinates are metres, so normally this path rescales nothing. It still
  // has to honour an overridden unit, for a source file that was authored in something
  // else. The wrapper is added only when it would do something, to leave the node
  // hierarchy of a straight round-trip exactly as it was.
  const root = scaleToMeters === 1 ? scene : new THREE.Group()
  if (root !== scene) {
    root.scale.setScalar(scaleToMeters)
    scene.add(root)
  }

  for (const obj of objects) {
    const src = obj.sourceAttributes!
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(src.position), 3))
    if (src.normal) {
      geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(src.normal), 3))
    }
    // Prefer the original UVs; fall back to the baked pattern UVs when the source
    // GLB had none (common for STEP-derived models). Without this the exported
    // GLB would carry a pattern texture but no UVs to sample it with.
    const exportUv = src.uv ?? obj.mesh.attributes.uv?.array
    if (exportUv) {
      geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(exportUv), 2))
    }
    // Tangents, where the source had them: a normal map is only as correct as the frame
    // it is read in, and a UV atlas that mirrors islands cannot be reconstructed from
    // screen-space derivatives alone.
    if (src.tangent) {
      geometry.setAttribute('tangent', new THREE.BufferAttribute(new Float32Array(src.tangent), 4))
    }
    if (src.index) {
      geometry.setIndex(new THREE.BufferAttribute(new Uint32Array(src.index), 1))
    }

    const material = await materialFor(obj, objects, materialProps)
    const mesh = new THREE.Mesh(geometry, material)
    mesh.name = obj.id
    tagLogoNode(mesh, obj, scaleToMeters)

    // Restore the original node placement (translation/rotation/scale).
    const matrix = new THREE.Matrix4().fromArray(obj.sourceTransform!)
    matrix.decompose(mesh.position, mesh.quaternion, mesh.scale)

    // Apply the user's move (world-space translation, same units as the preview).
    if (obj.offset) {
      mesh.position.x += obj.offset[0]
      mesh.position.y += obj.offset[1]
      mesh.position.z += obj.offset[2]
    }

    root.add(mesh)
  }

  tagStudio(scene, displayUnit)
  const result = await exporter.parseAsync(scene, { binary: true })
  const blob = new Blob([result as ArrayBuffer])
  await saveBlob(blob, suggestedName, 'model/gltf-binary', '.glb')
}

/**
 * Save a blob, prompting the user with a native "Save As" dialog when the
 * File System Access API is available. Falls back to a normal download on
 * browsers that don't support it (e.g. Firefox/Safari).
 */
async function saveBlob(blob: Blob, suggestedName: string, mimeType: string, extension: string): Promise<void> {
  const picker = (
    window as unknown as {
      showSaveFilePicker?: (opts: unknown) => Promise<{
        createWritable: () => Promise<{
          write: (data: Blob) => Promise<void>
          close: () => Promise<void>
        }>
      }>
    }
  ).showSaveFilePicker

  if (picker) {
    try {
      const handle = await picker({
        suggestedName,
        types: [
          {
            description: `${extension.slice(1).toUpperCase()} file`,
            accept: { [mimeType]: [extension] },
          },
        ],
      })
      const writable = await handle.createWritable()
      await writable.write(blob)
      await writable.close()
      return
    } catch (err) {
      // User cancelled the dialog — do nothing.
      if (err instanceof DOMException && err.name === 'AbortError') return
      // Any other failure: fall through to the download fallback.
    }
  }

  downloadBlob(blob, suggestedName)
}

function downloadBlob(blob: Blob, filename: string): void {
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}
